import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import DataGrid from "./common/AutoHeightDataGrid";
import AladinView from "./AladinView";
import useResizeObserver from "../hooks/useResizeObserver";
import "react-data-grid/lib/styles.css";
import "./style/ephem-table.css";


/**
 * Component to display ephemerids in a table format using react-data-grid.
 * @param {ephemerids[]} array of ephemerids
 * @returns
 */
export default function EphemTable({ephemerids, cameraSampling = 1.055, fovSize}) {
  const [filters] = useState({});
  const [selectedRowIdx, setSelectedRowIdx] = useState(null);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const { height: containerHeight } = useResizeObserver(containerRef);
  const [tableHeight, setTableHeight] = useState(null);

  const WARNING_H = 32;
  const SPLITTER_H = 8;
  const MIN_TABLE_H = 180;
  const MIN_MAP_H = 180;


  // Define columns
  const columns = useMemo(
    () => [
      {
        key: "date",
        name: "Date, Time (UTC)",
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: ({ row }) => // UTC date formatting
          new Date(row.date).toLocaleString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
          })
      },
      { key: "ra", name: "R.A.", resizable: true, sortable: true, filterable: true },
      { key: "dec", name: "Dec", resizable: true, sortable: true, filterable: true },
      { key: "elong", name: "Elong", resizable: true, sortable: true, filterable: true },
      { key: "v", name: "Vmag", resizable: true, sortable: true, filterable: true },
      { key: "motion", name: "Motion(/min) ", resizable: true, sortable: true, filterable: true },
      { key: "pa", name: "P.A.", resizable: true, sortable: true, filterable: true },

      {
        key: "maxExposure",
        name: "Max Exp (s)",
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: ({ row }) => {
          const motionValue = Number(row.motion);
          const samplingValue = Number(cameraSampling);
          if (!Number.isFinite(motionValue) || !Number.isFinite(samplingValue) || motionValue <= 0 || samplingValue <= 0) {
            return "n/a";
          }
          return ((samplingValue * 60) / motionValue).toFixed(1);
        }
      },
      {
        key: "mapOffsets",
        name: "Map/Offsets",
        resizable: true,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <span>
            {row.mapUrl
              ? <a href={row.mapUrl} target="_blank" rel="noreferrer">Map</a>
              : <span style={{color:'#aaa'}}>Map</span>}
            {' / '}
            {row.offsetsUrl
              ? <a href={row.offsetsUrl} target="_blank" rel="noreferrer">Offsets</a>
              : <span style={{color:'#aaa'}}>Offsets</span>}
          </span>
        )
      }
    ],
    [cameraSampling]
  );

  // Filtering logic
  const filteredRows = useMemo(() => {
    const rows = ephemerids?.ephem ?? [];
    return rows.filter((row) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        return String(row[key]).toLowerCase().includes(String(value).toLowerCase());
      });
    });
  }, [ephemerids, filters]);


  // Enhance columns with filter renderer
  const columnsWithFilters = columns.map((col) => ({
    ...col
  }));

  const suppressedCount = ephemerids?.suppressedCount ?? 0;

  const handleCellClick = useCallback(({ rowIdx }) => {
    setSelectedRowIdx(prev => (prev === rowIdx ? null : rowIdx));
  }, []);

  const selectedRow = selectedRowIdx != null ? filteredRows[selectedRowIdx] ?? null : null;

  useEffect(() => {
    if (!containerHeight || tableHeight != null) {
      return;
    }
    setTableHeight(Math.round(containerHeight * 0.55));
  }, [containerHeight, tableHeight]);

  useEffect(() => {
    if (!containerHeight || tableHeight == null) {
      return;
    }

    const maxTableHeight = Math.max(
      MIN_TABLE_H,
      containerHeight - MIN_MAP_H - SPLITTER_H
    );
    const nextHeight = Math.min(Math.max(tableHeight, MIN_TABLE_H), maxTableHeight);

    if (nextHeight !== tableHeight) {
      setTableHeight(nextHeight);
    }
  }, [containerHeight, tableHeight]);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!isDragging.current || !containerHeight) {
        return;
      }

      const maxTableHeight = Math.max(
        MIN_TABLE_H,
        containerHeight - MIN_MAP_H - SPLITTER_H
      );
      const delta = event.clientY - dragStartY.current;
      const nextHeight = Math.min(
        Math.max(dragStartHeight.current + delta, MIN_TABLE_H),
        maxTableHeight
      );

      setTableHeight(nextHeight);
    };

    const onMouseUp = () => {
      isDragging.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [containerHeight]);

  const onSplitterMouseDown = useCallback((event) => {
    if (tableHeight == null) {
      return;
    }

    isDragging.current = true;
    dragStartY.current = event.clientY;
    dragStartHeight.current = tableHeight;
    event.preventDefault();
  }, [tableHeight]);

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top half: table + warning banner */}
      <div style={{ flex: 'none', height: tableHeight ?? undefined, minHeight: MIN_TABLE_H, display: 'flex', flexDirection: 'column', paddingBottom: 6 }}>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <DataGrid
            className="rdg-light"
            columns={columnsWithFilters}
            rows={filteredRows}
            defaultColumnOptions={{ resizable: true, sortable: true }}
            onCellClick={handleCellClick}
            rowClass={(row, idx) => idx === selectedRowIdx ? 'ephem-row-selected' : undefined}
          />
        </div>
        <div style={{ flexShrink: 0, height: WARNING_H, display: 'flex', alignItems: 'center' }}>
          {suppressedCount > 0 && (
            <div style={{ padding: '4px 8px', background: '#fff3cd', color: '#856404', borderRadius: 4, fontSize: '0.85em', width: '100%' }}>
              ⚠️ {suppressedCount} row{suppressedCount > 1 ? 's' : ''} with suppressed data (not shown)
            </div>
          )}
        </div>
      </div>
      <div className="ephem-splitter" onMouseDown={onSplitterMouseDown}>
        <div className="ephem-splitter-bar" />
      </div>
      {/* Bottom half: Aladin sky view */}
      <div style={{ flex: 1, minHeight: MIN_MAP_H, overflow: 'hidden', paddingTop: 6 }}>
        <AladinView rows={filteredRows} selectedRow={selectedRow} fovSize={fovSize} />
      </div>
    </div>
  );
}

